/** Copy of the labels from the events module (the web app does not import server modules). */
export const EVENT_STATUSES = {
  zapytanie: 'Zapytanie',
  wycena: 'Wycena',
  potwierdzone: 'Potwierdzone',
  w_realizacji: 'W realizacji',
  zakonczone: 'Zakończone',
  rozliczone: 'Rozliczone',
  anulowane: 'Anulowane',
  rezygnacja: 'Rezygnacja klienta',
} as const;
export const EVENT_SCOPES = {
  dzwiek: 'Dźwięk kompletny',
  dostarczenie: 'Dostarczenie sprzętu',
  realizacja_foh: 'Realizacja FOH',
  realizacja_mon: 'Realizacja MON',
  technika: 'Technika',
  realizacja_nagrania: 'Realizacja nagrania',
  realizacja: 'Tylko realizacja',
  swiatlo: 'Światło',
  scena: 'Scena',
  video: 'Video',
  inne: 'Inne',
} as const;
/** Shown under "Więcej" in the scope picker (owner remark 7); "Inne" only when an older event has it. */
export const MORE_SCOPES: readonly EventScope[] = ['swiatlo', 'scena', 'video', 'inne'];
/** The usual answer to "Skąd sprzęt" (owner remark 3/15): chosen from the start, so "Dalej" is on at once. */
export const DEFAULT_EQUIPMENT_SOURCE = 'Z magazynu Mek Records';
/** P15 (owner's note 23:24): no gear of ours — the new event wizard skips its packlist step. */
export const NO_GEAR_SOURCE = 'Bez sprzętu';
/** Owner remark 2: "wstępna" / "pewna" instead of "miękka" / "twarda". */
export const RESERVATION_LABELS = { soft: 'Wstępna', hard: 'Pewna' } as const;

export type EventStatus = keyof typeof EVENT_STATUSES;

/** Closed without taking place (P6): "Anulowane" and "Rezygnacja klienta" — hidden in the calendar unless asked for. */
export const CLOSED_STATUSES: readonly EventStatus[] = ['anulowane', 'rezygnacja'];
export const isClosed = (status: string | undefined | null): boolean => (CLOSED_STATUSES as readonly string[]).includes(status ?? '');
/** "Tylko potwierdzone" (owner remark 15): confirmed and the statuses after it. */
export const CONFIRMED_STATUSES: readonly EventStatus[] = ['potwierdzone', 'w_realizacji', 'zakonczone', 'rozliczone'];
/** The statuses "Oznacz jako zakończone" starts from (as the server's `FINISHABLE_STATUSES`). */
export const FINISHABLE_STATUSES: readonly EventStatus[] = ['potwierdzone', 'w_realizacji'];

/**
 * The "Status" line of the details (owner 2026-10-20 23:42: "do wyceny czy w trakcie akceptacji klienta (po wysłaniu),
 * potwierdzona, zakończona"): the stage in words; a quote whose newest version is sent and not decided waits for the client.
 */
export function statusLine(status: EventStatus, awaitingClient = false): string {
  if (status === 'zapytanie') return 'Do wyceny';
  if (status === 'wycena' && awaitingClient) return 'Czeka na akceptację klienta';
  return EVENT_STATUSES[status];
}

/** "Oznacz jako zakończone" by hand: a confirmed or running event, from its first day (Warsaw) on — "dopiero w dniu wydarzenia". */
export function mayFinishByHand(e: { status?: EventStatus | undefined; startsAt?: string | undefined }, today: string): boolean {
  if (!e.status || !e.startsAt || !FINISHABLE_STATUSES.includes(e.status)) return false;
  return warsawParts(e.startsAt).day <= today;
}
export type EventScope = keyof typeof EVENT_SCOPES;
export type ReservationKind = keyof typeof RESERVATION_LABELS;

/** As the API sends it. A field the user may not see is simply absent. */
/** F28 (owner remark 25): what the event is. */
export type EventKind = 'impreza' | 'wypozyczenie';
export const EVENT_KINDS: Record<EventKind, string> = { impreza: 'Wydarzenie', wypozyczenie: 'Wypożyczenie' };

/**
 * „Rozliczenie” (owner 2026-11-03 23:33): with our invoice, or without one in a form of the payment plan. Financial data:
 * absent for whoever was not given the field.
 */
export type Settlement = 'faktura' | 'gotowka' | 'umowa' | 'przelew';
export const SETTLEMENT_LABELS: Record<Settlement, string> = { faktura: 'Faktura', gotowka: 'Bez faktury – gotówka', umowa: 'Bez faktury – umowa o dzieło', przelew: 'Bez faktury – przelew' };
/** The marker of an event settled without an invoice („Bez faktury – gotówka”); null for „Faktura” or a hidden field. */
export const settlementMarker = (s: Settlement | undefined): string | null => (s && s !== 'faktura' ? SETTLEMENT_LABELS[s] : null);
/** Why „Wystaw fakturę” is blocked for the event (the short reason by the button); null when it is not. */
export const settlementBlocksInvoice = (s: Settlement | undefined): string | null =>
  s && s !== 'faktura' ? 'Wydarzenie jest rozliczane bez faktury – nie wystawia się do niego faktury. Zmień „Rozliczenie” w wydarzeniu na „Faktura”.' : null;

export type EventView = {
  id: string;
  name?: string;
  contractorId?: string | null;
  startsAt?: string;
  endsAt?: string;
  /**
   * Several separate terms (owner 2026-10-21 00:44), only for an event with at least two: sorted, spanning
   * `startsAt`–`endsAt` (the whole range); gear and people are free in between. Absent: one term, the range.
   */
  segments?: EventSegment[];
  location?: string;
  /** Address and point of the place (owner remark 3/12); `distanceKm`: "ok. N km od magazynu", read only. */
  locationAddress?: string;
  locationPoint?: { lat: number; lng: number } | null;
  locationApprox?: boolean;
  distanceKm?: number | null;
  /** Where `distanceKm` comes from: the chosen route, typed route, Google route (read only); null: no km. */
  distanceSource?: 'chosen' | 'route' | 'google' | null;
  /** "km wg trasy" typed from the route in Google Maps; wins over the counted distance. */
  routeKm?: number | null;
  /** The automatic distance (the Google route), whatever was chosen or typed; read only (owner 2026-10-23). */
  autoDistance?: { km: number; source: 'google' } | null;
  /** Spec 2026-10-22-mapy-google: the place in "Miejsca wydarzeń", its Google id and notes. */
  venueId?: string | null;
  googlePlaceId?: string;
  placeNotes?: string;
  /** Where the place of "Miejsca wydarzeń" is (with the point visible): "Nawiguj" leaves out a Google id the point has left (review I4). */
  placePoint?: { lat: number; lng: number };
  /** "Unikaj płatnych"; the route chosen in "Pokaż trasę" (its tolls absent for whoever may not see them). */
  avoidTolls?: boolean;
  chosenRoute?: { km: number; minutes: number; tollsGrosze?: number | null; label: string } | null;
  /** Read only: the travel time (minutes) and tolls (grosze, one way) of the route that counts. */
  travelMinutes?: number | null;
  tollsGrosze?: number | null;
  status?: EventStatus;
  /** F28: an event we play ("impreza") or gear rented to another company ("wypozyczenie"). */
  kind?: EventKind;
  needsQuote?: boolean;
  /** „Rozliczenie”: absent without the field (financial data). */
  settlement?: Settlement;
  reservationKind?: ReservationKind;
  /** "Rezerwuj sprzęt także: N dni przed / po" (owner 2026-10-21 06:47): set by hand, 0 by default. */
  reserveDaysBefore?: number;
  reserveDaysAfter?: number;
  /** An entry of the systems list (`/api/events/systems`). */
  systemId?: string | null;
  equipmentSource?: string;
  /** Fixed scope codes (`EventScope`) and ids of custom scopes. */
  scopes?: string[];
  managerUserId?: string | null;
  /** The name of the person responsible ("Odpowiedzialny"), sent with `managerUserId` by a single event's answer. */
  managerName?: string | null;
  /** "Kto widzi" (spec 2026-10-23-widocznosc-wydarzen): the accounts on the list and their names (hidden together). */
  viewerIds?: string[];
  viewerNames?: string[];
  notes?: string;
  /** "Rezygnacja klienta" (P6): a short note and the day ('YYYY-MM-DD'). */
  resignationNote?: string;
  resignationOn?: string | null;
  version: number;
};

/** One term of an event (ISO instants, end exclusive). */
export type EventSegment = { startsAt: string; endsAt: string };

export const EVENTS_KEY = ['events'] as const;
export const EVENTS_RESOURCE = 'events.events';

// ---------- dates: days and times are always Warsaw time, never the browser's zone ----------

const WARSAW_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function warsawFields(ms: number): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const p = Object.fromEntries(WARSAW_PARTS.formatToParts(new Date(ms)).map((x) => [x.type, Number(x.value)]));
  return { y: p.year!, mo: p.month!, d: p.day!, h: p.hour!, mi: p.minute!, s: p.second! };
}

/** Warsaw offset from UTC at that instant, in ms. */
function warsawOffset(ms: number): number {
  const f = warsawFields(ms);
  return Date.UTC(f.y, f.mo - 1, f.d, f.h, f.mi, f.s) - Math.floor(ms / 1000) * 1000;
}

/**
 * Warsaw wall-clock day ("YYYY-MM-DD") and time ("HH:mm", default 00:00) -> ISO instant (UTC).
 * Takes the offset of the guessed instant, then checks it again at the result (handles the DST switch days).
 */
export function warsawInstant(day: string, time = '00:00'): string {
  const [y, mo, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const wall = Date.UTC(y!, mo! - 1, d!, h!, mi!);
  let ms = wall - warsawOffset(wall);
  ms = wall - warsawOffset(ms);
  return new Date(ms).toISOString();
}

export function addDays(day: string, n: number): string {
  const [y, mo, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y!, mo! - 1, d! + n)).toISOString().slice(0, 10);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant -> Warsaw day and time. */
export function warsawParts(iso: string): { day: string; time: string } {
  const f = warsawFields(new Date(iso).getTime());
  return { day: `${f.y}-${pad(f.mo)}-${pad(f.d)}`, time: `${pad(f.h)}:${pad(f.mi)}` };
}

/** Form values -> API instants. No end time: the event lasts to the end of the last day (start of the next day, exclusive). */
export function datesToInstants(d: { firstDay: string; firstTime: string; lastDay: string; lastTime: string }): { startsAt: string; endsAt: string } {
  return {
    startsAt: warsawInstant(d.firstDay, d.firstTime || '00:00'),
    endsAt: d.lastTime ? warsawInstant(d.lastDay, d.lastTime) : warsawInstant(addDays(d.lastDay, 1)),
  };
}

/** API instants -> form values (inverse of `datesToInstants`; an end at midnight means "whole last day"). */
export function instantsToDates(startsAt: string | undefined, endsAt: string | undefined): { firstDay: string; firstTime: string; lastDay: string; lastTime: string } {
  if (!startsAt || !endsAt) return { firstDay: '', firstTime: '', lastDay: '', lastTime: '' };
  const s = warsawParts(startsAt);
  const e = warsawParts(endsAt);
  return {
    firstDay: s.day,
    firstTime: s.time === '00:00' ? '' : s.time,
    lastDay: e.time === '00:00' ? addDays(e.day, -1) : e.day,
    lastTime: e.time === '00:00' ? '' : e.time,
  };
}

const DAY_LABEL = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long', year: 'numeric' });
const dayLabel = (day: string) => DAY_LABEL.format(new Date(warsawInstant(day, '12:00')));
/** "15 października 2026" for a 'YYYY-MM-DD' day. */
export const formatDay = dayLabel;

/** "10 lipca 2099 – 11 lipca 2099", with hours when they were given. */
export function formatEventRange(startsAt: string | undefined, endsAt: string | undefined): string {
  if (!startsAt || !endsAt) return '—';
  const v = instantsToDates(startsAt, endsAt);
  const first = `${dayLabel(v.firstDay)}${v.firstTime ? `, ${v.firstTime}` : ''}`;
  const last = `${dayLabel(v.lastDay)}${v.lastTime ? `, ${v.lastTime}` : ''}`;
  return first === last ? first : `${first} – ${last}`;
}

// ---------- several terms (owner 2026-10-21 00:44: days picked with Shift / Cmd make one event) ----------

/** Runs of following days (sorted, without repeats): each is one term of an event. */
export function runsOf(days: readonly string[]): Array<{ first: string; last: string }> {
  const runs: Array<{ first: string; last: string }> = [];
  for (const day of [...new Set(days)].sort()) {
    const run = runs.at(-1);
    if (run && addDays(run.last, 1) === day) run.last = day;
    else runs.push({ first: day, last: day });
  }
  return runs;
}

/**
 * Terms from picked days, as the server's `segmentsFromDays`: each run from `firstTime` on its first day (midnight
 * without it) to `lastTime` on its last day; an end hour not after the start hour (16:00–02:00) is past midnight after
 * the last day; without an end hour the whole last day.
 */
export function segmentsFromDays(days: readonly string[], firstTime = '', lastTime = ''): EventSegment[] {
  return runsOf(days).map(({ first, last }) => ({
    startsAt: warsawInstant(first, firstTime || '00:00'),
    endsAt: lastTime ? warsawInstant(lastTime <= (firstTime || '00:00') ? addDays(last, 1) : last, lastTime) : warsawInstant(addDays(last, 1)),
  }));
}

/** Every term in words ("2 listopada 2026; 5 listopada 2026, 10:00 – 6 listopada 2026, 18:00"). */
export function formatSegments(segments: readonly EventSegment[]): string {
  return segments.map((t) => formatEventRange(t.startsAt, t.endsAt)).join('; ');
}

/**
 * The first whole day after a term ends (review minor 4: "Dodaj termin" must not overlap a night term): an end hour
 * not after the start hour of a one-day term is past midnight, as the server reads it.
 */
export function nextTermDay(t: { firstDay: string; lastDay: string; firstTime: string; lastTime: string }): string {
  const night = t.lastTime !== '' && t.lastDay === t.firstDay && t.lastTime <= (t.firstTime || '00:00');
  const end = warsawParts(night ? warsawInstant(addDays(t.lastDay, 1), t.lastTime) : datesToInstants(t).endsAt);
  return end.time === '00:00' ? end.day : addDays(end.day, 1);
}
